import { createSlice } from "@reduxjs/toolkit";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, createMapSelector, compareGroupedMap } from "../common/reducers/utilities";
import { maxDate, minDate } from "../common/utils/dateUtils";
import { distinct, ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched } from "../common/hooks/initHook";

const key = wh => wh.rosterid
const compareRosters = (a, b) => key(a) === key(b);

export const workHoursSlice = createSlice({
    name: 'workHours',
    initialState: {
        data: {}
    },
    reducers: {
        addOrUpdate: (state, action) => {
            const byMember = groupBy(ensureArray(action.payload), 'memberid');
            Object.entries(byMember).forEach(([memberid, hours]) => {
                if (state.data[memberid]) {
                    const existing = state.data[memberid];
                    hours.forEach(h => {
                        h.startTs = (new Date(h.start)).getTime();
                        h.endTs = (new Date(h.end)).getTime();
                        let idx = existing.findIndex(note => compareRosters(h, note));
                        if (idx >= 0) {
                            existing.splice(idx, 1, h);
                        } else {
                            existing.push(h);
                        }
                    });
                } else {
                    state.data[memberid] = hours.map(h => ({ ...h, startTs: (new Date(h.start)).getTime(), endTs: (new Date(h.end)).getTime() }));
                }
            });
        },
        remove: (state, action) => {
            const byMember = groupBy(ensureArray(action.payload), 'memberid');
            Object.entries(byMember).forEach(([memberid, hours]) => {
                const ids = new Set(hours.map(h => h.rosterid));
                state.data[memberid] = state.data[memberid].filter(h => !ids.has(h.rosterid));
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => workHoursSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'workHours', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'workHours');

const fetchWorkHoursInRange = aggregatingFetch(
    (start, end) => network => network.post(`/api/v2/workHoursInRange/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, null, workHoursSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post(`/api/v2/workHoursInRange/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, null, workHoursSlice.actions.addOrUpdate, visualizeFailure('get'))
);

const fetchMemberWorkHoursInRange = aggregatingFetch(
    (memberid, start, end) => network => {
        return network.post(`/api/v2/workHoursInRange/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, [memberid], data => workHoursSlice.actions.addOrUpdate(data), visualizeFailure('get'))
    },
    keys => network => network.post(`/api/v2/workHoursInRange/${encodeURIComponent(minDate(keys, 1))}/${encodeURIComponent(maxDate(keys, 2))}`, distinct(keys.map(key => key[0])), data => workHoursSlice.actions.addOrUpdate(data), visualizeFailure('get'))
);
// #endregion

// #region selectors
const selectWorkHoursData = state => state.workHours.data;
const selectWorkHoursInRangeMap = (start, end) => createMapSelector([selectWorkHoursData, () => start, () => end], (workHours, start, end) => {
    const s = new Date(start), e = new Date(end);
    const overlap = a => s <= a.endTs && a.startTs <= e;
    
    const result = {};
    for (const key in workHours) {
        const filtered = workHours[key].filter(a => overlap(a));
        if (filtered.length > 0) {
            result[key] = filtered;
        }
    }
    return result;
});

const selectMemberWorkHoursInRangeMap = (memberid, start, end) => createMapSelector([selectWorkHoursData, () => memberid, () => start, () => end], (workHours, memberid, start, end) => {
    const s = new Date(start), e = new Date(end);
    const overlap = a => s <= a.endTs && a.startTs <= e;
    return {[memberid]: workHours[memberid]?.filter(a => overlap(a)) ?? []};
});
// #endregion

const listen = {
    workHoursCreated: (workHours, { dispatch, websocket }) => {
        markFetched(websocket, 'work_hours', key(workHours))
        dispatch(workHoursSlice.actions.addOrUpdate(workHours))
    },
    workHoursUpdated: (workHours, { dispatch }) => dispatch(workHoursSlice.actions.addOrUpdate(workHours)),
    workHoursRemoved: (workHours, { dispatch }) => dispatch(workHoursSlice.actions.remove(workHours))
}

const memberWorkHoursInitByRange = initMapFactory(fetchMemberWorkHoursInRange, listen, { name: 'memberWorkHoursByRange' });
const cleanup = { root: workHoursSlice.name, all: state => Object.values(selectWorkHoursData(state)).flat(), removeAll: workHoursSlice.actions.remove, key };
export const [useMemberWorkHoursInRangeMap] = dataHookFactory(selectMemberWorkHoursInRangeMap, memberWorkHoursInitByRange, {cleanup, comparator: compareGroupedMap });
export const [useWorkHoursInRangeMap] = dataHookFactory(selectWorkHoursInRangeMap, queryMapFactory(fetchWorkHoursInRange, 'memberid', memberWorkHoursInitByRange, listen, { keyBuilder: (pkey, data, args) => [data[pkey], ...args] }), {cleanup, comparator: compareGroupedMap });

const reducer = { name: workHoursSlice.name, reducer: workHoursSlice.reducer };
export default reducer;